import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../config';
import { Company, FinancialData, ComparisonData, ApiResponse } from '../types';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

export const apiService = {
  // 全企業一覧取得
  async getCompanies(): Promise<Company[]> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = API_BASE_URL + API_ENDPOINTS.companies;
      console.log('Fetching companies from:', url);
      
      xhr.open('GET', url, true);
      // シンプルなGETリクエスト（ヘッダーなし）でプリフライトリクエストを完全に回避
      
      xhr.onreadystatechange = function() {
        console.log(`XHR readyState: ${xhr.readyState}, status: ${xhr.status}, statusText: ${xhr.statusText}`);
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              console.log('Companies response:', data);
              resolve(data.companies || []);
            } catch (parseError) {
              console.error('JSON parse error:', parseError);
              reject(new Error('Failed to parse response'));
            }
          } else if (xhr.status === 0) {
            console.error('Network or CORS error - status 0');
            console.error('Response text:', xhr.responseText);
            console.error('All response headers:', xhr.getAllResponseHeaders());
            reject(new Error('CORS or network error (status 0)'));
          } else {
            console.error('HTTP error:', xhr.status, xhr.statusText);
            console.error('Response text:', xhr.responseText);
            reject(new Error(`HTTP error! status: ${xhr.status}`));
          }
        }
      };
      
      xhr.onerror = function() {
        console.error('Network error');
        reject(new Error('Network error'));
      };
      
      xhr.send();
    });
  },

  // 企業検索（部分一致）
  async searchCompanies(query: string): Promise<Company[]> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = API_BASE_URL + API_ENDPOINTS.search(query);
      
      xhr.open('GET', url, true);
      // シンプルなGETリクエスト（ヘッダーなし）でプリフライトリクエストを完全に回避
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data.companies || []);
            } catch (parseError) {
              reject(new Error('Failed to parse response'));
            }
          } else {
            reject(new Error(`HTTP error! status: ${xhr.status}`));
          }
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.send();
    });
  },

  // 証券コードで企業の財務データを直接取得
  async getCompanyDataBySecurityCode(securityCode: string, year?: string): Promise<FinancialData> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let url = API_BASE_URL + API_ENDPOINTS.companyByCode(securityCode);
      if (year) {
        url += `?year=${year}`;
      }
      
      xhr.open('GET', url, true);
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (parseError) {
              reject(new Error('Failed to parse response'));
            }
          } else {
            reject(new Error(`HTTP error! status: ${xhr.status}`));
          }
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.send();
    });
  },

  // 個別企業データ取得
  async getCompanyData(companyName: string, year?: string): Promise<FinancialData> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let url = API_BASE_URL + API_ENDPOINTS.company(companyName);
      if (year) {
        url += `?year=${year}`;
      }
      
      xhr.open('GET', url, true);
      // シンプルなGETリクエスト（ヘッダーなし）でプリフライトリクエストを完全に回避
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (parseError) {
              reject(new Error('Failed to parse response'));
            }
          } else {
            reject(new Error(`HTTP error! status: ${xhr.status}`));
          }
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.send();
    });
  },

  // 企業比較データ取得
  async compareCompanies(companyNames: string[]): Promise<ComparisonData[]> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = API_BASE_URL + API_ENDPOINTS.compare(companyNames);
      
      xhr.open('GET', url, true);
      // シンプルなGETリクエスト（ヘッダーなし）でプリフライトリクエストを完全に回避
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data.companies || []);
            } catch (parseError) {
              reject(new Error('Failed to parse response'));
            }
          } else {
            reject(new Error(`HTTP error! status: ${xhr.status}`));
          }
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.send();
    });
  },

  // Gemini企業分析取得
  async getCompanyAnalysis(companyName: string, year?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const encodedName = encodeURIComponent(companyName);
      let url = `${API_BASE_URL}/analysis/${encodedName}`;
      if (year) {
        url += `?year=${year}`;
      }
      
      xhr.open('GET', url, true);
      // タイムアウトを60秒に設定（Gemini APIの応答が遅い可能性があるため）
      xhr.timeout = 60000;
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (parseError) {
              reject(new Error('Failed to parse analysis response'));
            }
          } else if (xhr.status === 408) {
            reject(new Error('分析処理がタイムアウトしました。しばらくしてから再試行してください。'));
          } else {
            reject(new Error(`分析取得エラー: ${xhr.status}`));
          }
        }
      };
      
      xhr.ontimeout = function() {
        reject(new Error('分析処理がタイムアウトしました。しばらくしてから再試行してください。'));
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error during analysis'));
      };
      
      xhr.send();
    });
  },
};